import React from 'react'
import { Card, CardContent, Box, Typography, Avatar, SxProps, Theme } from '@mui/material'
import { TrendingUp, TrendingDown } from '@mui/icons-material'

export interface MobileMetricCardProps {
  title: string
  value: string | number
  icon?: React.ReactNode
  subtitle?: string
  trend?: number
  trendLabel?: string
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info'
  gradient?: string
  onClick?: () => void
  sx?: SxProps<Theme>
}

const gradients = {
  primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  success: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
  warning: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
  error: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
  info: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
}

/**
 * Mobile Metric Card Component
 * Displays a large metric with optional trend indicator
 * Perfect for dashboard KPIs
 */
export const MobileMetricCard: React.FC<MobileMetricCardProps> = ({
  title,
  value,
  icon,
  subtitle,
  trend,
  trendLabel,
  color = 'primary',
  gradient,
  onClick,
  sx,
}) => {
  const showTrend = trend !== undefined
  const isPositiveTrend = trend && trend > 0
  const isNegativeTrend = trend && trend < 0

  return (
    <Card
      onClick={onClick}
      sx={{
        background: gradient || gradients[color],
        color: 'white',
        borderRadius: '8px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:active': {
          transform: 'scale(0.98)',
        },
        '&:hover': onClick
          ? {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            }
          : {},
        ...sx,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          {/* Title */}
          <Typography
            variant="body2"
            sx={{
              opacity: 0.9,
              fontWeight: 500,
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {title}
          </Typography>

          {/* Icon */}
          {icon && (
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                '& .MuiSvgIcon-root': {
                  fontSize: 24,
                },
              }}
            >
              {icon}
            </Avatar>
          )}
        </Box>

        {/* Value */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.75rem', sm: '2rem' },
            lineHeight: 1.2,
            mb: 1,
          }}
        >
          {value}
        </Typography>

        {/* Subtitle and Trend */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                opacity: 0.9,
                fontSize: '0.875rem',
              }}
            >
              {subtitle}
            </Typography>
          )}

          {showTrend && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                px: 1,
                py: 0.5,
              }}
            >
              {isPositiveTrend && <TrendingUp sx={{ fontSize: 16 }} />}
              {isNegativeTrend && <TrendingDown sx={{ fontSize: 16 }} />}
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              >
                {isPositiveTrend && '+'}
                {trend}%
              </Typography>
              {trendLabel && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    opacity: 0.9,
                  }}
                >
                  {trendLabel}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}
